import { useMemo, useState } from "react";
import type { GitCommit } from "@foreman/protocol";
import { layoutGraph } from "./git-graph";
import { GraphCell } from "./git-graph-view";

// What a locked feature looks like, drawn from made-up data, so a guest sees what signing in gives them. The demo is read only,
// but the tabs work: a guest can look around Changes, Branches and Graph before deciding.

const NOW = Math.floor(Date.now() / 1000);
const h = (n: number) => NOW - n * 3600;

const COMMITS: GitCommit[] = [
  { hash: "a1", short: "a1f3c92", subject: "Add rate limiting to /login", author: "You", at: h(1), parents: ["b2"], refs: ["feature/rate-limit"] },
  { hash: "b2", short: "b27d0e1", subject: "Merge branch 'fix/login-redirect'", author: "You", at: h(5), parents: ["c3", "d4"], refs: ["main", "origin/main"] },
  { hash: "d4", short: "d4a90b7", subject: "Fix redirect loop after sign-in", author: "You", at: h(9), parents: ["c3"], refs: ["fix/login-redirect"] },
  { hash: "c3", short: "c3e5518", subject: "Update dependencies", author: "Ana", at: h(30), parents: ["e5"] },
  { hash: "e5", short: "e58b2a4", subject: "Set up the project", author: "Ana", at: h(72), parents: [] },
];

const BRANCHES = [
  { name: "main", current: true, sub: "Merge branch 'fix/login-redirect' · 5h ago", sync: "↑0 ↓0" },
  { name: "feature/rate-limit", current: false, sub: "Add rate limiting to /login · 1h ago", sync: "↑2 ↓0" },
  { name: "fix/login-redirect", current: false, sub: "Fix redirect loop after sign-in · 9h ago", sync: "↑0 ↓0" },
  { name: "experiment/agents", current: false, sub: "Try a second agent · 3d ago", sync: "local only" },
];

const FILES: [string, string, string][] = [
  ["M", "src/server/routes/auth.ts", "+18 −4"],
  ["A", "src/server/middleware/rateLimit.ts", "+42"],
  ["M", "test/auth.test.ts", "+9 −1"],
  ["M", "package.json", "+1 −1"],
];

const ago = (t: number) => {
  const m = Math.max(1, Math.round((NOW - t) / 60));
  return m < 60 ? `${m}m ago` : m < 48 * 60 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};

type Tab = "changes" | "branches" | "history";
const TABS: { name: Tab; label: string }[] = [
  { name: "changes", label: "Changes" },
  { name: "branches", label: "Branches" },
  { name: "history", label: "Graph" },
];

/** Source Control with sample data: the same three tabs a signed-in phone has. */
export function GitDemo() {
  const [tab, setTab] = useState<Tab>("changes");
  const graph = useMemo(() => layoutGraph(COMMITS), []);
  return (
    <div className="demo">
      <div className="demo-chip mono">Example · sample data</div>
      <div className="git-tabs" role="tablist" aria-label="Source Control example">
        {TABS.map((t) => (
          <button key={t.name} role="tab" aria-selected={t.name === tab} className="git-tab" onClick={() => setTab(t.name)}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="demo-body">
        {tab === "changes" && (
          <>
            <div className="card">
              {FILES.map(([k, f, c]) => (
                <div className="pv-row" key={f}>
                  <span className={"pv-k " + k}>{k}</span>
                  <span className="pv-f">{f}</span>
                  <span className="pv-c">{c}</span>
                </div>
              ))}
            </div>
            <div className="pv-bar">Commit message…</div>
          </>
        )}
        {tab === "branches" && (
          <div className="card">
            {BRANCHES.map((b) => (
              <div className="row git-branch" key={b.name}>
                <span className="git-branch-main">
                  <span className="git-branch-title">
                    {b.current && <span className="git-current">●</span>}
                    {b.name}
                  </span>
                  <span className="git-branch-sub">{b.sub}</span>
                </span>
                <span className={"git-sync" + (b.sync === "local only" ? " dim" : "")}>{b.sync}</span>
              </div>
            ))}
          </div>
        )}
        {tab === "history" && (
          <div className="card">
            {COMMITS.map((c, i) => (
              <div className="row git-commit git-graph-row" key={c.hash}>
                <GraphCell row={graph.rows[i]!} lanes={graph.lanes} />
                <span className="git-branch-main">
                  <span className="git-commit-subject git-graph-subject">
                    {c.subject}
                    {(c.refs ?? []).map((r) => (
                      <span className="git-ref" key={r}>
                        {r}
                      </span>
                    ))}
                  </span>
                  <span className="git-branch-sub">
                    <code>{c.short}</code> · {c.author} · {ago(c.at)}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
